import { Component, EventEmitter, Input, Output } from '@angular/core'
import { ApiService } from 'src/app/services/api.service'
import { NetworkSandboxService, SyncPhaseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { LaunchRestoreSettings, LaunchSyncDialogBase } from 'src/app/account/components/launch-sync-dialog-base'
import { FormBuilder, Validators } from '@angular/forms'
import { ValidationOutput } from 'src/app/shared/string-list-control/string-list-control.component'
import { validCidr, validDomain } from '../../../shared/utils'
import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

interface Suggestion {
  value: string
  reason: string
}

const DEFAULT_AWS_SUGGESTED_DOMAINS: Suggestion[] = [{ value: '.amazonaws.com', reason: 'All AWS services' }]
const DEFAULT_AZURE_SUGGESTED_DOMAINS: Suggestion[] = [
  { value: 'management.azure.com', reason: 'Azure resource management APIs' },
  { value: '.vault.azure.net', reason: 'All Azure key vaults' },
  { value: '.blob.core.windows.net', reason: 'All Azure storage account blobs' },
]

@Component({
  selector: 'launch-test-dialog',
  templateUrl: './launch-test-dialog.component.html',
  styleUrls: ['./launch-test-dialog.component.scss'],
  standalone: false,
})
export class LaunchTestDialogComponent extends LaunchSyncDialogBase {
  @Input() currentlySyncing: boolean = false
  @Output() launch = new EventEmitter<Array<LaunchRestoreSettings>>()

  public whitelistPlaceholderText = '8.8.8.0/24, 34.0.0.8...'
  public domainAllowlistPlaceholderText = 'registry.hub.docker.com, quay.io'
  public advancedNetworkSandbox: boolean = false
  public sandboxFrozen: boolean = false
  public suggestedDomains: Suggestion[] = []

  private _whitelistIsValid: boolean = true
  private _domainAllowlistIsValid: boolean = true
  constructor(
    private formBuilder: FormBuilder,
    applicationProviderService: ApplicationProviderService,
    accountService: AccountService,
    apiService: ApiService,
    uiService: UiService,
    accessLevelService: AccessLevelService
  ) {
    super(applicationProviderService, accountService, apiService, uiService, accessLevelService)
  }

  launchTestSettingsFormModel = this.formBuilder.group({
    sandboxChecked: [false, Validators.required],
    // Use Array<string> here instead of [] to convey type :)
    sandboxWhitelist: [new Array<string>()],
    sandboxAllowedDomains: [new Array<string>()],
    allowS3Checked: [false],
    allowDynamoChecked: [false],
    forceNewTargetPlans: [false],
  })

  ngOnInit() {
    super.ngOnInit()
    this.advancedNetworkSandbox = this.application.advancedNetworkSandboxEnabled
    this.launchTestSettingsFormModel.setValue({
      sandboxChecked: this.application.networkSandboxEnabled,
      sandboxWhitelist: this.application.networkSandboxWhitelist,
      sandboxAllowedDomains: this.application.networkSandboxAllowedDomains,
      allowS3Checked: this.application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_S3),
      allowDynamoChecked: this.application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_DYNAMODB),
      forceNewTargetPlans: false,
    })

    const allowedDomains = new Set(this.application.networkSandboxAllowedDomains || [])
    const defaultDomains = this.isAzureApplication ? DEFAULT_AZURE_SUGGESTED_DOMAINS : DEFAULT_AWS_SUGGESTED_DOMAINS
    this.suggestedDomains = defaultDomains.filter((sd) => !allowedDomains.has(sd.value))
  }

  onLaunchClick(): void {
    this.launch.emit(
      this.selectedApplications.reduce((accum, app) => {
        let rp = this.computeApplicationRecoveryPoint(app)
        if (rp) {
          accum.push({
            application: app,
            recoveryPointId: rp.recoveryPointId,
            isRealTime: this.isRealTime && app.rpo === 0,
            networkSandboxEnabled: this.networkSandboxChecked,
            networkSandboxWhitelist: this.networkSandboxWhitelist,
            networkSandboxAllowedDomains: this.networkSandboxAllowedDomains,
            networkSandboxAllowedServices: this.networkSandboxAllowedServices,
            advancedNetworkSandbox: this.advancedNetworkSandbox,
            forceNewTargetPlan: this.forceNewTargetPlans,
          } as LaunchRestoreSettings)
        }
        return accum
      }, [] as Array<LaunchRestoreSettings>)
    )
  }

  protected applicationIsCandidate(application: Application) {
    // If another app in the same sync pair (or region pair for multi) is in FAILOVER_TEST
    // then we cannot change the network sandbox behaviour. The network sandbox
    // for this application will be set to be the same as the existing
    // application in FAILOVER_TEST
    if (application.syncPhase === SyncPhaseDto.FAILOVER_TEST) {
      this.sandboxFrozen = true
      this.launchTestSettingsFormModel.setValue({
        sandboxChecked: application.networkSandboxEnabled,
        sandboxWhitelist: application.networkSandboxWhitelist,
        sandboxAllowedDomains: application.networkSandboxAllowedDomains,
        allowS3Checked: application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_S3),
        allowDynamoChecked: application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_DYNAMODB),
        forceNewTargetPlans: false,
      })
      // The disable here doesn't work for the whitelist,
      // so we do it inside string-list-control
      this.launchTestSettingsFormModel.get('sandboxChecked').disable()
      this.launchTestSettingsFormModel.get('allowS3Checked').disable()
      this.launchTestSettingsFormModel.get('allowDynamoChecked').disable()
    }
    return application.syncPhase === SyncPhaseDto.STANDBY
  }

  get networkSandboxChecked(): boolean {
    return this.launchTestSettingsFormModel.get('sandboxChecked').value
  }

  get networkSandboxWhitelist(): string[] {
    return this.launchTestSettingsFormModel.get('sandboxWhitelist').value
  }

  get networkSandboxAllowedDomains(): string[] {
    return this.launchTestSettingsFormModel.get('sandboxAllowedDomains').value
  }

  get networkSandboxAllowedServices(): string[] {
    let allowedServices = []

    if (this.launchTestSettingsFormModel.get('allowS3Checked').value) {
      allowedServices.push(NetworkSandboxService.AWS_S3)
    }

    if (this.launchTestSettingsFormModel.get('allowDynamoChecked').value) {
      allowedServices.push(NetworkSandboxService.AWS_DYNAMODB)
    }

    return allowedServices
  }

  get forceNewTargetPlans(): boolean {
    return this.launchTestSettingsFormModel.get('forceNewTargetPlans').value
  }

  public isValidCidr(candidate: string): boolean {
    return validCidr(candidate)
  }

  public isValidDomain(domain: string): boolean {
    return validDomain(domain)
  }

  public normalizeCidr(candidate: string): string {
    return candidate.includes('/') ? candidate : candidate + '/32'
  }

  whitelistValidated(event: ValidationOutput): void {
    this._whitelistIsValid = !event.invalid
  }

  sandboxDomainsValidated(event: ValidationOutput): void {
    this._domainAllowlistIsValid = !event.invalid
  }

  setAdvancedNetworkSandbox(value: boolean): void {
    this.advancedNetworkSandbox = value
  }

  get showAdvancedSandboxOptions(): boolean {
    return this.accountService.advancedNetworkSandboxEnabled && this.networkSandboxChecked
  }

  get showAdvancedOptions(): boolean {
    return this.accountService.entitledForCapability('allowForceNewTargetPlan')
  }

  get canLaunchTest(): boolean {
    // if appGroupRestore, then check application is in standby, and
    if (this.accountService.entitledForCapability('appGroupRestore')) {
      return (
        !this.launching &&
        (!this.currentlySyncing || this.application.syncPhase === SyncPhaseDto.STANDBY) &&
        this._whitelistIsValid &&
        this._domainAllowlistIsValid
      )
    }
    // Else default to previous behaviour
    return !this.launching && !this.currentlySyncing && this._whitelistIsValid
  }

  get currentlySyncingMessage() {
    if (this.accountService.entitledForCapability('appGroupRestore')) {
      return this.currentlySyncing && this.application.syncPhase !== SyncPhaseDto.STANDBY
    }
    return this.currentlySyncing
  }

  addAllowedDomain(domain: string) {
    const control = this.launchTestSettingsFormModel.get('sandboxAllowedDomains')
    let value = control.value || []
    value.push(domain)
    control.setValue(value)
    this.suggestedDomains = this.suggestedDomains.filter((s) => s.value != domain)
  }

  protected getRequiredPermission(): AppPermission {
    return AppPermission.MANAGE_TEST
  }

  get isAzureApplication(): boolean {
    return this.application?.type === 'azure'
  }
}
